import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import MuiModal from "../../muiComps/MuiModal";
import UpdateUserDetailsForm from "./UpdateUserDetailsForm";

// Per-plan feature config
const PLAN_CONFIG = {
  LITE: {
    icon: <FlashOnIcon sx={{ fontSize: 28, color: "#64748b" }} />,
    iconBg: "#f1f5f9",
    validity: "3 Month Plan",
    color: "#64748b",
    features: [
      "RA Calls (CASH + FUTURES)",
      "300 RA Automated Signals",
      "Signal Access — NSE Equity",
      "Stock Screener",
      "Stock Charts",
    ],
    originalPrice: null,
  },
  PLUS: {
    icon: <RocketLaunchIcon sx={{ fontSize: 28, color: "#7c3aed" }} />,
    iconBg: "#ede9fe",
    validity: "6 Month Plan",
    color: "#7c3aed",
    features: [
      "RA Calls (CASH + FUTURES)",
      "600 RA Automated Signals",
      "Signal Access — NSE Equity",
      "NIFTY / BANKNIFTY Signals",
      "Stock Screener & Charts",
      "Watchlist & Alerts",
    ],
    originalPrice: null,
  },
  PRO: {
    icon: <StarIcon sx={{ fontSize: 28, color: "#016551" }} />,
    iconBg: "#dcfce7",
    validity: "1 Year Plan",
    color: "#016551",
    features: [
      "RA Calls (CASH + FUTURES)",
      "1000 RA Automated Signals",
      "Signal Access — NSE Equity",
      "NIFTY / BANKNIFTY / FINNIFTY",
      "MCX — Gold, Silver, Natural Gas",
      "Stock Screener & Charts",
      "Watchlist & Alerts",
      "Priority Support",
    ],
    originalPrice: 4999,
    badge: "Most Popular",
  },
};

const PricingTileNew = ({ tier, planPeriod, planUpgradeType }) => {
  const navigate = useNavigate();
  const [openUserUpdateForm, setOpenUserUpdateForm] = useState(false);

  const planKey = (tier.name || "").toUpperCase();
  const config = PLAN_CONFIG[planKey] || {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 28, color: "#016551" }} />,
    iconBg: "#dcfce7",
    validity: "",
    color: "#016551",
    features: [],
    originalPrice: null,
  };

  const isPopular = !!config.badge;

  const YEARLY_DISCOUNT = 0.2;
  const displayPrice =
    planPeriod === "yearly"
      ? Math.round(tier.price * 12 * (1 - YEARLY_DISCOUNT))
      : tier.price;

  const handleClickNavigate = () => {
    navigate("/cart", { state: { planType: planUpgradeType, selectedPlan: tier } });
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          background: isPopular
            ? "linear-gradient(145deg,#04343a 0%,#016551 100%)"
            : "#fff",
          borderRadius: "20px",
          border: isPopular ? "none" : "1.5px solid #e2e8f0",
          boxShadow: isPopular
            ? "0 20px 50px rgba(1,101,81,0.35)"
            : "0 4px 16px rgba(0,0,0,0.07)",
          p: 3.5,
          flex: "1 1 260px",
          maxWidth: 320,
          minWidth: 240,
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.22s, box-shadow 0.22s",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: isPopular
              ? "0 28px 60px rgba(1,101,81,0.45)"
              : "0 10px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Most Popular badge */}
        {isPopular && (
          <Box
            sx={{
              position: "absolute",
              top: -14,
              left: "50%",
              transform: "translateX(-50%)",
              background: "linear-gradient(135deg,#f59e0b,#d97706)",
              borderRadius: "20px",
              px: 2.5,
              py: 0.6,
              boxShadow: "0 4px 12px rgba(245,158,11,0.4)",
              whiteSpace: "nowrap",
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.72rem", letterSpacing: 1 }}>
              ★ MOST POPULAR
            </Typography>
          </Box>
        )}

        {/* Icon + Plan name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              background: isPopular ? "rgba(255,255,255,0.12)" : config.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {React.cloneElement(config.icon, {
              sx: { fontSize: 26, color: isPopular ? "#a3e635" : config.color },
            })}
          </Box>
          <Box>
            <Typography
              sx={{
                color: isPopular ? "#fff" : "#04343a",
                fontWeight: 900,
                fontSize: "1.15rem",
                letterSpacing: 1,
                textTransform: "uppercase",
                lineHeight: 1.1,
              }}
            >
              {tier.name}
            </Typography>
            <Typography
              sx={{
                color: isPopular ? "rgba(255,255,255,0.55)" : "#94a3b8",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {config.validity}
            </Typography>
          </Box>
        </Box>

        {/* Price */}
        <Box sx={{ mb: 2.5 }}>
          {config.originalPrice && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
              <Typography sx={{ color: isPopular ? "rgba(255,255,255,0.4)" : "#94a3b8", fontSize: "0.82rem", textDecoration: "line-through" }}>
                ₹{config.originalPrice.toLocaleString()}
              </Typography>
              <Box sx={{ background: "rgba(220,38,38,0.2)", borderRadius: "6px", px: 1, py: 0.2 }}>
                <Typography sx={{ color: "#ef4444", fontWeight: 700, fontSize: "0.65rem" }}>
                  ₹{(config.originalPrice - tier.price).toLocaleString()} OFF
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
            <Typography
              sx={{
                color: isPopular ? "#a3e635" : "#04343a",
                fontWeight: 900,
                fontSize: "2.4rem",
                lineHeight: 1,
                letterSpacing: -1,
              }}
            >
              ₹{displayPrice.toLocaleString()}
            </Typography>
            <Typography sx={{ color: isPopular ? "rgba(255,255,255,0.45)" : "#94a3b8", fontSize: "0.8rem", mb: 0.4 }}>
              /{planPeriod === "yearly" ? "yr" : "plan"}
            </Typography>
          </Box>
          {tier.description ? (
            <Typography sx={{ color: isPopular ? "rgba(255,255,255,0.5)" : "#64748b", fontSize: "0.75rem", mt: 0.5 }}>
              {tier.description}
            </Typography>
          ) : null}
        </Box>

        {/* Divider */}
        <Box sx={{ height: 1, background: isPopular ? "rgba(255,255,255,0.1)" : "#f1f5f9", mb: 2.5 }} />

        {/* Features */}
        <Box sx={{ flex: 1, mb: 3 }}>
          {config.features.map((feat, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.2 }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 16,
                  color: isPopular ? "#a3e635" : "#016551",
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  color: isPopular ? "rgba(255,255,255,0.8)" : "#475569",
                  fontSize: "0.82rem",
                  lineHeight: 1.5,
                }}
              >
                {feat}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CTA Button */}
        <Box
          component="button"
          onClick={handleClickNavigate}
          sx={{
            width: "100%",
            border: "none",
            borderRadius: "12px",
            py: 1.6,
            fontWeight: 800,
            fontSize: "0.9rem",
            cursor: "pointer",
            letterSpacing: 0.5,
            transition: "all 0.22s",
            background: isPopular
              ? "linear-gradient(135deg,#a3e635,#84cc16)"
              : "transparent",
            color: isPopular ? "#04343a" : "#016551",
            border: isPopular ? "none" : "2px solid #016551",
            boxShadow: isPopular ? "0 6px 18px rgba(163,230,53,0.4)" : "none",
            "&:hover": isPopular
              ? { background: "linear-gradient(135deg,#bef264,#a3e635)", transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(163,230,53,0.5)" }
              : { background: "#016551", color: "#fff" },
          }}
        >
          {planUpgradeType === "update" ? "Change Plan" : "Choose Plan →"}
        </Box>
      </Box>

      <MuiModal open={openUserUpdateForm} setOpen={setOpenUserUpdateForm}>
        <UpdateUserDetailsForm />
      </MuiModal>
    </>
  );
};

export default PricingTileNew;
